import { renderIsoCodeMappingInput } from '@host/pages/IsoCodeMapping/IsoCodeMappingWrapper'
import { EquipmentSelectionViewStore } from '@host/pages/TallymanV2/EquipmentSelectionViewStore'
import { Box } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  equipmentService,
  HatchCoverMove,
  Stoppage,
  Stoppages,
  TallymanEquipmentSelector,
  useOperationsStore,
  workInstructionService,
} from '@tom-ui/operations'
import {
  ContainerDamageRecordStep,
  ContainerOperations,
  ContainerRailOperations,
  containerService,
  EquipmentSelection,
  InspectContainer,
  mapFormDataToOverlandedContainerVisitCommand,
  ReportAccidentallyDischargedContainer,
  SearchContainers,
  SearchRob,
  StowagePositionStep,
  TallymanOperationStoppedAlert,
  useNavigationStore,
  usePlanningStore,
  VisitDetails,
  WorkAssignmentsSelection,
} from '@tom-ui/planning'
import {
  CarrierVisitDirection,
  CheckType,
  ContainerPositionType,
  JourneyCategory,
  StowagePositionDto,
} from '@tom-ui/planning/app/api'
import { AssignedCraneForContainerDto, EquipmentPlannedDto } from 'modules/operations/src/app/api'
import { useMemo } from 'react'

export const useVesselTallyStepperActions = () => {
  const { t } = useTranslate()
  const navigationStore = useNavigationStore()

  const { tallymanSelectVesselAndOperationViewStore: store, drawerStore } = usePlanningStore()

  const { vesselTallyStore, railTallyStore } = store

  const { searchContainerStore, checkNotificationStore, reportNotificationStore } = vesselTallyStore

  const { tallymanOperationsUIStore } = useOperationsStore()

  const equipmentSelectionStore = useMemo(
    () =>
      new EquipmentSelectionViewStore(
        tallymanOperationsUIStore,
        vesselTallyStore.checkNotificationStore,
      ),
    [tallymanOperationsUIStore, vesselTallyStore],
  )

  const fetchEquipmentsByVisitId = async (visitId: number) => {
    if (visitId) {
      const equipments: EquipmentPlannedDto[] = await equipmentService.loadPlanned(visitId)
      vesselTallyStore.setCranes(
        equipments.filter(x => x.equipmentType === 'STS').map(x => ({ id: x.id, name: x.name })),
      )

      const assignments = await equipmentService.getAssignedCranesForContainers(visitId)
      vesselTallyStore.setAssignedCraneForOrder(
        assignments.map((x: AssignedCraneForContainerDto) => ({
          craneId: x.craneId,
          craneName: x.craneName,
          orderId: x.orderId,
        })),
      )
    }
  }

  const fetchPlannedEquipmentsByCraneId = async (craneId: number) => {
    if (!vesselTallyStore.selectedVisitId) return

    tallymanOperationsUIStore.loadBy(vesselTallyStore.selectedVisitId, craneId)
  }

  const openDrawer = () => {
    drawerStore.showView(
      <Box padding={1.5}>
        <WorkAssignmentsSelection
          store={store}
          onSelectTrainOperation={operation => {
            navigationStore.clear()
            onSelectTrainOperation(operation)
            drawerStore.close()
          }}
          onSelectVesselOperation={operation => {
            navigationStore.clear()
            onSelectVesselOperation(operation)
            drawerStore.close()
          }}
          onVesselSelection={fetchEquipmentsByVisitId}
          onCraneSelection={fetchPlannedEquipmentsByCraneId}
          renderIsoCodeMappingInput={renderIsoCodeMappingInput}
        />
      </Box>,
      {},
      'left',
      true,
    )
  }

  const onSelectTrainOperation = (op: CheckType) => {
    railTallyStore.selectOperationType(op)
    navigationStore.push(<ContainerRailOperations openDrawer={openDrawer} store={railTallyStore} />)
  }

  const onWorkAssignmentsSelection = () => {
    navigationStore.push(
      <WorkAssignmentsSelection
        store={store}
        onSelectVesselOperation={onSelectVesselOperation}
        onSelectTrainOperation={onSelectTrainOperation}
        renderIsoCodeMappingInput={renderIsoCodeMappingInput}
        onVesselSelection={fetchEquipmentsByVisitId}
        onCraneSelection={fetchPlannedEquipmentsByCraneId}
      />,
    )
  }

  const onSubmitCheckedContainer = async (
    orderData: any,
    selectedCraneId?: number,
    stowagePosition?: StowagePositionDto,
  ) => {
    store.setSubmittedSeals(orderData.seals ?? [], orderData.hasSeals)
    checkNotificationStore.createCheckInRequest(
      orderData,
      searchContainerStore.selectedOrder?.data,
      tallymanOperationsUIStore,
      selectedCraneId,
      store.selectedGang?.id,
      searchContainerStore.selectedOrder?.journeyCategory === JourneyCategory.Restow,
      stowagePosition,
    )

    navigationStore.clear()
    onContainerOperations()
  }

  const onReportContainer = () => {
    if (vesselTallyStore.selectedVisitId)
      navigationStore.push(
        <ReportAccidentallyDischargedContainer
          visitId={vesselTallyStore.selectedVisitId}
          onSubmit={onSubmitReportedContainer}
          onClose={navigationStore.pop}
        />,
      )
  }

  const onSubmitReportedContainer = (cmd: any) => {
    reportNotificationStore.createReportRequest(cmd)

    navigationStore.clear()
    onContainerOperations()
  }

  const onContainerOperations = () => {
    navigationStore.push(
      <ContainerOperations
        store={store}
        onSearchClick={onSearchContainers}
        onCancelCheckRequest={onCancelCheckRequest}
        openDrawer={openDrawer}
        operationStoppedComponent={
          <TallymanOperationStoppedAlert onWorkAssignmentsSelection={onWorkAssignmentsSelection} />
        }
      />,
    )
  }

  const onSearchContainers = () => {
    searchContainerStore.setSearchFilter('')
    checkNotificationStore.reset()
    reportNotificationStore.reset()

    navigationStore.push(
      <SearchContainers
        store={vesselTallyStore.searchContainerStore}
        onContainerCardClick={onSelectContainer}
        onReportClick={onReportContainer}
        onSearchInRobClick={onSearchInRob}
        operationStoppedComponent={
          <TallymanOperationStoppedAlert onWorkAssignmentsSelection={onWorkAssignmentsSelection} />
        }
        tallymanOperationsUIStore={tallymanOperationsUIStore}
      />,
    )
  }

  const onSearchInRob = () => {
    vesselTallyStore.searchRobStore.setSearchFilter('')
    checkNotificationStore.reset()
    reportNotificationStore.reset()

    navigationStore.push(
      <SearchRob
        store={vesselTallyStore.searchRobStore}
        onContainerCardClick={onSelectContainer}
        operationStoppedComponent={
          <TallymanOperationStoppedAlert onWorkAssignmentsSelection={onWorkAssignmentsSelection} />
        }
      />,
    )
  }

  const onCancelCheckRequest = () => {
    if (checkNotificationStore.order) checkNotificationStore.order.status = 'Open'
    equipmentSelectionStore.cancelRequest()
  }

  const handleOverlandedContainerCreation = async (data: any) => {
    const mappedData = mapFormDataToOverlandedContainerVisitCommand(data)
    const id = await containerService.createOverlandedContainerVisit(mappedData)
    searchContainerStore.selectOrder(id)
    return id
  }

  const onSelectContainer = (id?: number) => {
    searchContainerStore.selectOrder(id)

    navigationStore.push(
      <InspectContainer
        store={searchContainerStore}
        submitButtonText={t('next', 'Next')}
        lastSubmittedSeals={store.lastSubmittedSeals}
        onSubmit={async data => {
          if (data.hasDamage) return onReportDamage(data)

          let orderId = searchContainerStore.selectedOrder?.data.id

          if (orderId === undefined) {
            orderId = await handleOverlandedContainerCreation(data)
          }

          const order = searchContainerStore.selectedOrder

          if (order?.data.direction === CarrierVisitDirection.Inbound)
            return onNavigateToSelectEquipment({ ...data, id: orderId })
          else if (order?.data.direction === CarrierVisitDirection.Outbound)
            return onNavigateToUpdateStowagePosition({ ...data, id: orderId })

          return onSubmitCheckedContainer(
            { ...data, id: orderId },
            vesselTallyStore.selectedCrane?.id,
          )
        }}
      />,
    )
  }

  const onReportDamage = (orderData: any) => {
    const { selectedOrder: order } = searchContainerStore

    navigationStore.push(
      <ContainerDamageRecordStep
        orderData={orderData}
        containerNumber={order?.data.containerNumber}
        hasNextStep={true}
        direction={order?.data.direction}
        onSubmit={(data, damages) => {
          data.damagesReported = damages

          return order?.data.direction === 'Inbound'
            ? onNavigateToSelectEquipment(data)
            : onNavigateToUpdateStowagePosition(data)
        }}
      />,
    )
  }

  const onNavigateToSelectEquipment = (orderData: any) => {
    tallymanOperationsUIStore.setSelectedOrderId(orderData.id)
    navigationStore.push(
      <EquipmentSelection
        orderData={orderData}
        tallymanViewStore={vesselTallyStore}
        tallymanOperationsUIStore={tallymanOperationsUIStore}
        onGetWorkInstruction={orderId =>
          workInstructionService.getWorkInstructionByExternalOrderId(orderId)
        }
        onSelectEquipment={(orderData, order) => onSelectEquipment(orderData, order)}
        renderEquipmentSelector={params => (
          <TallymanEquipmentSelector
            vesselVisitId={params.vesselVisitId}
            craneName={params.craneName}
            showAlert={params.showAlert}
            journeyCategory={params.journeyCategory}
          />
        )}
      />,
    )
  }

  const onNavigateToUpdateStowagePosition = (
    orderData: any,
    stowagePosition?: StowagePositionDto,
  ) => {
    const { selectedOrder: order } = searchContainerStore

    navigationStore.push(
      <StowagePositionStep
        tallymanOperationsUIStore={tallymanOperationsUIStore}
        orderData={orderData}
        containerNumber={order?.data.containerNumber}
        onSubmit={data => {
          // if the order is inbound, we don't want to update the stowage position
          if (order?.data.direction === CarrierVisitDirection.Outbound)
            orderData.finalStowagePosition = data

          onSubmitCheckedContainer(orderData, vesselTallyStore.selectedCrane?.id, data)
        }}
        stowagePosition={stowagePosition}
      />,
    )
  }

  const onSelectEquipment = async (orderData: any, order: any) => {
    if (tallymanOperationsUIStore.selectedContainerPosition === ContainerPositionType.Vessel) {
      const stowagePosition = searchContainerStore.getOutboundStowagePositionForRestow(
        orderData.containerNumber,
      )
      onNavigateToUpdateStowagePosition(orderData, stowagePosition)
    } else {
      store.setSubmittedSeals(orderData.seals ?? [], orderData.hasSeals)
      equipmentSelectionStore.checkOrder(
        orderData,
        order?.data,
        vesselTallyStore.selectedCrane?.id,
        store.selectedGang?.id,
        searchContainerStore.selectedOrder?.journeyCategory === JourneyCategory.Restow,
      )
      navigationStore.clear()
      onContainerOperations()
    }
  }

  const resetNavigationState = () => {
    navigationStore.clear()
    onWorkAssignmentsSelection()
  }

  const onRecordStoppage = (stoppage?: any) => {
    navigationStore.push(
      <Stoppage
        vesselVisitId={vesselTallyStore.selectedVisit!.id}
        visitName={vesselTallyStore.selectedVisit!.vessels[0]?.data?.name}
        stoppage={stoppage}
        onClose={() => navigationStore.pop()}
        onRecordStoppage={resetNavigationState}
        selectedCrane={
          { ...vesselTallyStore.selectedCrane, equipmentType: 'STS' } as EquipmentPlannedDto
        }
      />,
    )
  }

  const onSelectVesselOperation = (op: any) => {
    vesselTallyStore.selectOperation(op)

    if (vesselTallyStore.selectedVisit) {
      if (op === 'ShowVisitDetails') {
        navigationStore.push(
          <VisitDetails
            item={vesselTallyStore.selectedVisit}
            onBack={() => navigationStore.pop()}
            onWorkAssignmentsSelection={onWorkAssignmentsSelection}
          />,
        )
      } else if (op === 'Stoppage') {
        navigationStore.push(
          <Stoppages
            vesselVisitId={vesselTallyStore.selectedVisit.id}
            visitName={vesselTallyStore.selectedVisit.vessels[0]?.data?.name}
            openDrawer={openDrawer}
            onRecordStoppage={onRecordStoppage}
            selectedCrane={
              { ...vesselTallyStore.selectedCrane, equipmentType: 'STS' } as EquipmentPlannedDto
            }
            operationStoppedComponent={
              <TallymanOperationStoppedAlert
                onWorkAssignmentsSelection={onWorkAssignmentsSelection}
              />
            }
            vesselTallyStore={vesselTallyStore}
          />,
        )
      } else if (op === 'HatchCoverMove') {
        navigationStore.push(
          <HatchCoverMove
            vesselVisitId={vesselTallyStore.selectedVisit.id}
            selectedGangId={store.selectedGang?.id}
            title={vesselTallyStore.selectedVisit.vessels[0]?.data?.name}
            openDrawer={openDrawer}
            onRecordHatchCoverMove={resetNavigationState}
            selectedCrane={
              { ...vesselTallyStore.selectedCrane, equipmentType: 'STS' } as EquipmentPlannedDto
            }
            operationStoppedComponent={
              <TallymanOperationStoppedAlert
                onWorkAssignmentsSelection={onWorkAssignmentsSelection}
              />
            }
            vesselTallyStore={vesselTallyStore}
          />,
        )
      } else {
        onContainerOperations()
      }
    }
  }

  return { onWorkAssignmentsSelection }
}
